/**
 * @file blendInclusion.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-09-17
 * @modified 2026-09-17
 * @description What counts as a material being IN a blend — one rule, shared by
 *              the solver, the brief builder and everything that draws a blend.
 *
 *              THERE IS NO DEFAULT MINIMUM INCLUSION
 *
 *              A counted ingredient used to have to be at least half a percent,
 *              so "minimum 4 ingredients" padded a blend with 0.5% each of cheap
 *              materials. That was a house rule, not a feed rule: an inclusion of
 *              a few hundredths of a percent is a real one. A minimum is now only
 *              ever what the request asked for ("each ingredient at least 1%").
 *
 *              What is left is a TECHNICAL floor — the smallest amount the blend
 *              can actually carry — so that "in" and "out" still mean something
 *              and a pivot's 1e-9 is never an ingredient:
 *
 *                - Inclusions are held to two decimals of a percent. The solver
 *                  rounds its answer to them, and the grid, the brief panel and
 *                  every export show them. 0.01% is the finest step.
 *                - The printed sheet drops anything at or under PRINT_THRESHOLD
 *                  (0.01%) — the workbook's IF(quantity > 0.01, …), which
 *                  blendMath deliberately keeps. An ingredient at exactly 0.01%
 *                  would be counted on screen and then vanish from the sheet the
 *                  mill works from.
 *
 *              So a material is in the blend when it would print, and the floor
 *              is the smallest two-decimal inclusion that does: 0.02%, two
 *              hundred grams a tonne. Derived from PRINT_THRESHOLD rather than
 *              written down, so the two cannot drift apart.
 */

// Explicit extension: scripts/check-blend-solver.mjs imports the solver, and
// through it this, straight into bare node.
import * as blendMath from './blendMath.js';

/** Inclusions are percentages to this many decimals. */
export const INCLUSION_DECIMALS = 2;

/**
 * Is this inclusion part of the blend?
 *
 * Exactly the printed sheet's test, so what is counted, what is listed and what
 * is printed always agree. Zero, blank, negative and solver noise are all out.
 */
export function isIncluded(value) {
  const n = blendMath.toNumber(value);
  return n !== null && n > blendMath.PRINT_THRESHOLD;
}

/**
 * The smallest inclusion that is part of the blend: the first two-decimal step
 * above PRINT_THRESHOLD. 0.02% today.
 *
 * Used as the floor for a counted ingredient when the request set no minimum of
 * its own, and as the least any stated minimum can be.
 */
export const INCLUSION_FLOOR =
  (Math.floor(blendMath.PRINT_THRESHOLD * 10 ** INCLUSION_DECIMALS + 1e-9) + 1) / 10 ** INCLUSION_DECIMALS;

/**
 * The most a stated minimum inclusion can be. Past half the blend, "every
 * ingredient at least X%" allows one ingredient at most, which is not a blend —
 * a figure like that has been misread, not asked for.
 */
export const MAX_MIN_INCLUSION = 50;

/** How many materials are actually in a blend. */
export function countIngredients(values) {
  return Object.values(values || {}).filter(isIncluded).length;
}

/**
 * A percentage said the way it was asked for: 1, 0.5, 0.024 — never 1.00 or
 * 0.02 for a figure someone typed to three places.
 */
export function formatInclusion(value) {
  const n = blendMath.toNumber(value);
  return n === null ? '' : String(Number(n.toFixed(3)));
}
